import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Play, ArrowLeft, Shuffle, List, X, BookOpen, Video, Volume2, Moon } from 'lucide-react';
import { Header } from '../layout/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';

export const KidStoriesPage = () => {
  const { kidId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();

  const [kid, setKid] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playlistOrder, setPlaylistOrder] = useState([]);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [viewerMode, setViewerMode] = useState('both');
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  // Check if we're in bedtime mode
  const isBedtimeMode = location.state?.bedtimeMode;

  useEffect(() => {
    const fetchKidStories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/recording/kid/${kidId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to fetch recordings');
        }

        const data = await response.json();
        setKid(data.kid);
        setRecordings(data.recordings);
        setPlaylistOrder(data.recordings.map((_, index) => index));
      } catch (error) {
        console.error('Error fetching kid stories:', error);
        showToast('Failed to load stories', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchKidStories();
  }, [kidId]);

  // Auto-play when in bedtime mode
  useEffect(() => {
    if (isBedtimeMode && recordings.length > 0 && !showViewer) {
      // Small delay to ensure everything is loaded
      const timer = setTimeout(() => {
        handlePlayAll();
        showToast('🌙 Bedtime stories starting...', 'success');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isBedtimeMode, recordings, showViewer]);

  const handlePlayAll = () => {
    if (recordings.length === 0) return;
    setCurrentlyPlaying(0);
    const firstRecording = recordings[playlistOrder[0]];
    setShowViewer(true);
    setViewerMode(firstRecording.is_ai ? 'book' : 'both');
    if (!isBedtimeMode) {
      showToast(`Playing all ${recordings.length} stories`, 'info');
    }
  };

  const handlePlaySingle = (playIndex) => {
    setCurrentlyPlaying(playIndex);
    const recording = recordings[playlistOrder[playIndex]];
    setShowViewer(true);
    setViewerMode(recording.is_ai ? 'book' : 'both');
    showToast(`Now playing: ${recording.book?.title || 'Untitled Story'}`, 'info');
  };

  const handleMediaEnded = () => {
    const nextIndex = currentlyPlaying + 1;
    if (nextIndex < recordings.length) {
      setCurrentlyPlaying(nextIndex);
      const recording = recordings[playlistOrder[nextIndex]];
      setViewerMode(recording.is_ai ? 'book' : 'both');
      showToast(`Next story: ${recording.book?.title || 'Untitled Story'}`, 'info');
    } else {
      setCurrentlyPlaying(null);
      setShowViewer(false);
      if (isBedtimeMode) {
        showToast('🌙 Bedtime stories finished! Sweet dreams!', 'success');
      } else {
        showToast('Playlist finished!', 'success');
      }
    }
  };

  const handlePrevious = () => {
    if (currentlyPlaying > 0) {
      const prevIndex = currentlyPlaying - 1;
      setCurrentlyPlaying(prevIndex);
      const recording = recordings[playlistOrder[prevIndex]];
      setViewerMode(recording.is_ai ? 'book' : 'both');
    }
  };

  const handleNext = () => {
    if (currentlyPlaying < recordings.length - 1) {
      const nextIndex = currentlyPlaying + 1;
      setCurrentlyPlaying(nextIndex);
      const recording = recordings[playlistOrder[nextIndex]];
      setViewerMode(recording.is_ai ? 'book' : 'both');
    } else {
      setCurrentlyPlaying(null);
      setShowViewer(false);
      if (isBedtimeMode) {
        showToast('🌙 Bedtime stories finished! Sweet dreams!', 'success');
      } else {
        showToast('Playlist finished!', 'success');
      }
    }
  };

  const closeViewer = () => {
    setShowViewer(false);
    setCurrentlyPlaying(null);
    
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Navigate back to parent dashboard with bedtimeMode set to false
    if (isBedtimeMode) {
      navigate('/parent-dashboard', { state: { bedtimeMode: false } });
    }
  };

  const shufflePlaylist = () => {
    const shuffled = [...playlistOrder].sort(() => Math.random() - 0.5);
    setPlaylistOrder(shuffled);
    showToast('Playlist shuffled!', 'success');
  };

  const resetPlaylistOrder = () => {
    setPlaylistOrder(recordings.map((_, index) => index));
    showToast('Playlist order reset', 'info');
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...playlistOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    setPlaylistOrder(newOrder);
  };

  const moveDown = (index) => {
    if (index === playlistOrder.length - 1) return;
    const newOrder = [...playlistOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setPlaylistOrder(newOrder);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading stories...</p>
      </div>
    );
  }

  if (!kid) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card style={{ padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
            Kid Not Found
          </h2>
          <Button onClick={() => navigate('/parent-dashboard')}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const avatarColors = ['#FF6B9D', '#4ECDC4', '#FFD93D', '#A8E6CF', '#FF8B94', '#C7CEEA'];
  const kidColor = avatarColors[kid.id % avatarColors.length];

  const currentRecording = currentlyPlaying !== null ? recordings[playlistOrder[currentlyPlaying]] : null;

  // Count video vs AI stories
  const videoCount = recordings.filter(r => !r.is_ai && !r.is_personal).length;
  const aiCount = recordings.filter(r => r.is_ai).length;
  const personalCount = recordings.filter(r => r.is_personal).length;


  return (
    <div style={{ minHeight: '100vh', background: isBedtimeMode ? 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' : 'linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%)' }}>
      <ToastContainer />
      <Header userName={`${user.first_name} ${user.last_name}`} userType="parent" />
      
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <Button
          onClick={() => navigate('/parent-dashboard')}
          variant="ghost"
          style={{ 
            marginBottom: '1rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            color: isBedtimeMode ? 'white' : undefined,
            background: isBedtimeMode ? 'rgba(255, 255, 255, 0.1)' : undefined
          }}
        >
          <ArrowLeft size={20} /> Back to Dashboard
        </Button>

        {/* Bedtime Mode Banner */}
        {isBedtimeMode && (
          <Card style={{ 
            padding: '1rem', 
            marginBottom: '1rem', 
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            border: '2px solid #fde68a',
            boxShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '0.75rem' 
            }}>
              <Moon size={24} color="white" />
              <p style={{ 
                color: 'white', 
                fontSize: '1.125rem', 
                fontWeight: 'bold',
                margin: 0,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                🌙 Bedtime Mode Active - Stories will play automatically
              </p>
              <Moon size={24} color="white" />
            </div>
          </Card>
        )}

        <Card style={{ 
          padding: '2rem', 
          marginBottom: '2rem', 
          background: isBedtimeMode ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))' : 'white',
          border: isBedtimeMode ? '1px solid rgba(255, 255, 255, 0.2)' : undefined
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${kidColor}, ${kidColor}dd)`,
                borderRadius: '50%',
                width: '100px',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: isBedtimeMode ? '0 0 20px rgba(251, 191, 36, 0.5)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                textTransform: 'uppercase',
                border: '4px solid white'
              }}>
                {kid.first_name.charAt(0)}
              </div>
              <div>
                <h1 style={{ 
                  fontSize: '2.25rem', 
                  fontWeight: 'bold', 
                  color: isBedtimeMode ? 'white' : '#1f2937', 
                  marginBottom: '0.5rem' 
                }}>
                  {kid.first_name}'s Stories
                </h1>
                <p style={{ 
                  fontSize: '1.125rem', 
                  color: isBedtimeMode ? '#e0e7ff' : '#6b7280', 
                  marginBottom: '0.25rem' 
                }}>
                  {recordings.length} {recordings.length === 1 ? 'story' : 'stories'} available
                </p>
                {(videoCount > 0 || aiCount > 0 || personalCount > 0) && (
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {videoCount > 0 && (
                      <span style={{
                        background: '#db2777',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}>
                        <Video size={14} /> {videoCount} Video
                      </span>
                    )}
                    {aiCount > 0 && (
                      <span style={{
                        background: '#8b5cf6',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}>
                        <Volume2 size={14} /> {aiCount} AI Audio
                      </span>
                    )}
                    {personalCount > 0 && (
                      <span style={{
                        background: '#10b981',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem'
                      }}>
                        <BookOpen size={14} /> {personalCount} Personal
                      </span>
                    )}
                  </div>
                )}

              </div>
            </div>

            {recordings.length > 0 && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Button
                  onClick={handlePlayAll}
                  style={{
                    background: isBedtimeMode ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : '#db2777',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    boxShadow: isBedtimeMode ? '0 4px 12px rgba(251, 191, 36, 0.4)' : undefined
                  }}
                >
                  {isBedtimeMode ? <Moon size={20} /> : <Play size={20} />} 
                  {isBedtimeMode ? 'Start Bedtime Stories' : 'Play All Stories'}
                </Button>
                <Button
                  onClick={() => setShowPlaylistModal(true)}
                  variant="secondary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    background: isBedtimeMode ? 'rgba(255, 255, 255, 0.1)' : undefined,
                    color: isBedtimeMode ? 'white' : undefined,
                    border: isBedtimeMode ? '1px solid rgba(255, 255, 255, 0.2)' : undefined
                  }}
                >
                  <List size={20} /> Edit Playlist
                </Button>
              </div>
            )}
          </div>
        </Card>

        {recordings.length === 0 ? (
          <Card style={{ padding: '3rem', textAlign: 'center' }}>
            <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '1rem' }}>
              No stories recorded yet for {kid.first_name}
            </p>
            <Button onClick={() => navigate('/parent-dashboard')}>
              Back to Dashboard
            </Button>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {playlistOrder.map((recordingIndex, playIndex) => {
                const recording = recordings[recordingIndex];
                const isAI = recording.is_ai;
                const isPersonal = recording.is_personal;
                
                return (
                  <Card
                    key={recording.id}
                    style={{
                      padding: '1.5rem',
                      background: currentlyPlaying === playIndex 
                        ? (isPersonal ? '#f0fdf4' : isAI ? '#f3e8ff' : '#fce7f3') 
                        : (isBedtimeMode 
                            ? (isPersonal ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05))' : isAI ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(109, 40, 217, 0.05))' : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))')
                            : (isPersonal ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : isAI ? 'linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)' : 'white')
                          ),
                      border: currentlyPlaying === playIndex 
                        ? (isPersonal ? '2px solid #10b981' : isAI ? '2px solid #8b5cf6' : '2px solid #db2777')
                        : (isPersonal ? '2px solid #10b981' : isBedtimeMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e5e7eb'),
                      boxShadow: currentlyPlaying === playIndex 
                        ? (isPersonal ? '0 8px 16px rgba(16, 185, 129, 0.2)' : isAI ? '0 8px 16px rgba(139, 92, 246, 0.2)' : '0 8px 16px rgba(219, 39, 119, 0.2)') 
                        : '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                  >
                    {/* Personal Badge */}
                    {isPersonal && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        zIndex: 1
                      }}>
                        <BookOpen size={10} /> PERSONAL
                      </div>
                    )}
                    
                    {/* AI Badge */}
                    {isAI && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        zIndex: 1
                      }}>
                        <span>🤖</span> AI VOICE
                      </div>
                    )}

                    {currentlyPlaying === playIndex && (
                      <div style={{
                        background: isPersonal ? '#10b981' : isAI ? '#8b5cf6' : '#db2777',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        marginBottom: '1rem',
                        display: 'inline-block'
                      }}>
                        NOW PLAYING
                      </div>
                    )}
                    
                    {/* Update the book preview section */}
                    <div style={{
                      background: isBedtimeMode ? 'rgba(0, 0, 0, 0.2)' : '#f3f4f6',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                      marginBottom: '1rem',
                      textAlign: 'center'
                    }}>
                      {isPersonal ? (
                        // Personal book cover
                        recording.book?.cover_image_url ? (
                          <img 
                            src={recording.book.cover_image_url}
                            alt={recording.book?.title}
                            style={{
                              width: '100%',
                              maxWidth: '200px',
                              height: '250px',
                              objectFit: 'cover',
                              borderRadius: '0.5rem',
                              border: '2px solid #10b981',
                              marginBottom: '0.75rem'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            maxWidth: '200px',
                            height: '250px',
                            margin: '0 auto',
                            marginBottom: '0.75rem',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            borderRadius: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '4rem',
                            color: 'white'
                          }}>
                            📚
                          </div>
                        )
                      ) : recording.book?.pdf_url ? (
                        // Regular book PDF preview
                        <div style={{ 
                          width: '100%', 
                          height: '200px', 
                          marginBottom: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          overflow: 'hidden',
                          backgroundColor: '#ffffff'
                        }}>
                          <iframe 
                            src={`${recording.book.pdf_url.trim()}#page=1&view=FitH`}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              border: 'none',
                              pointerEvents: 'none'
                            }}
                            title={`${recording.book.title} preview`}
                          />
                        </div>
                      ) : (
                        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📖</div>
                      )}
                      
                      <h3 style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: 'bold', 
                        color: isBedtimeMode ? 'white' : (isPersonal ? '#064e3b' : '#1f2937'), 
                        marginBottom: '0.25rem' 
                      }}>
                        {recording.book?.title || 'Untitled Story'}
                      </h3>
                      {recording.book?.author && (
                        <p style={{ 
                          fontSize: '0.875rem', 
                          color: isBedtimeMode ? '#c7d2fe' : (isPersonal ? '#047857' : '#6b7280'), 
                          fontStyle: 'italic' 
                        }}>
                          by {recording.book.author}
                        </p>
                      )}
                    </div>

                    {/* Update button styling */}
                    <Button
                      onClick={() => handlePlaySingle(playIndex)}
                      style={{
                        width: '100%',
                        background: isPersonal ? 'linear-gradient(135deg, #10b981, #059669)' : isAI ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : '#db2777',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {isPersonal ? (
                        recording.recording_type === 'audio_only' ? <Volume2 size={16} /> : <Play size={16} />
                      ) : isAI ? (
                        <Volume2 size={16} />
                      ) : (
                        <Play size={16} />
                      )}
                      {isPersonal ? 'Play Personal Story' : isAI ? 'Listen' : 'Play Story'}
                    </Button>
                  </Card>
                );
              })}
          </div>
        )}
      </div>

      {/* Video/Book/Audio Viewer Modal - remains the same */}
      {showViewer && currentRecording && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}>
          {/* Header */}
          <div style={{
            background: currentRecording.is_ai 
              ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' 
              : (isBedtimeMode ? 'linear-gradient(135deg, #1e1b4b, #312e81)' : 'rgba(0, 0, 0, 0.8)'),
            padding: '1rem 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div>
              {isBedtimeMode && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <Moon size={16} color="#fbbf24" />
                  <span style={{ 
                    color: '#fbbf24', 
                    fontSize: '0.875rem', 
                    fontWeight: '600' 
                  }}>
                    Bedtime Mode
                  </span>
                </div>
              )}
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>
                {currentRecording.book?.title || 'Untitled Story'}
              </h2>
              <p style={{ fontSize: '0.875rem', color: '#e5e7eb' }}>
                {currentRecording.is_ai 
                  ? `AI Voice: ${currentRecording.voice_name} (${currentRecording.reader?.first_name} ${currentRecording.reader?.last_name})`
                  : `Read by ${currentRecording.reader?.first_name} ${currentRecording.reader?.last_name}`
                }
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* View mode buttons - only show for video recordings */}
              {!currentRecording.is_ai && (
                <>
                  <Button
                    onClick={() => setViewerMode('video')}
                    variant={viewerMode === 'video' ? 'primary' : 'ghost'}
                    style={{
                      background: viewerMode === 'video' ? '#db2777' : 'transparent',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem'
                    }}
                  >
                    <Video size={16} /> Video Only
                  </Button>
                  <Button
                    onClick={() => setViewerMode('both')}
                    variant={viewerMode === 'both' ? 'primary' : 'ghost'}
                    style={{
                      background: viewerMode === 'both' ? '#db2777' : 'transparent',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 1rem'
                    }}
                  >
                    <BookOpen size={16} /> <Video size={16} /> Both
                  </Button>
                </>
              )}
              <Button
                onClick={closeViewer}
                variant="ghost"
                style={{ color: 'white', fontSize: '1.5rem', padding: '0.5rem 1rem' }}
              >
                <X size={24} />
              </Button>
            </div>
          </div>

          {/* Content Area */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            gap: '1rem', 
            padding: '1rem',
            overflow: 'hidden'
          }}>
            {/* AI Audio Player Section */}
            {currentRecording.is_ai && (
              <>
                {/* Book Section for AI */}
                <div style={{ 
                  flex: 1,
                  background: '#fff',
                  borderRadius: '0.5rem',
                  overflow: 'hidden'
                }}>
                  {currentRecording.book?.pdf_url ? (
                    <iframe
                      src={currentRecording.book.pdf_url.trim()}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                      title={currentRecording.book.title}
                    />
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      fontSize: '1.25rem',
                      color: '#6b7280'
                    }}>
                      No book PDF available
                    </div>
                  )}
                </div>

                {/* Audio Player Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: '5rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  padding: '1.5rem 2rem',
                  borderRadius: '1rem',
                  boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  minWidth: '400px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem'
                  }}>
                    🤖
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: 'white', fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
                      AI Audio Playing
                    </p>
                    <p style={{ color: '#e5e7eb', fontSize: '0.875rem' }}>
                      {currentRecording.voice_name}
                    </p>
                  </div>
                  <audio
                    ref={audioRef}
                    src={currentRecording.audio_url}
                    controls
                    autoPlay
                    onEnded={handleMediaEnded}
                    style={{
                      maxWidth: '300px'
                    }}
                  />
                </div>
              </>
            )}

            {/* Video Recording Section */}
            {!currentRecording.is_ai && (
              <>
                {/* Video Section */}
                {(viewerMode === 'video' || viewerMode === 'both') && (
                  <div style={{ 
                    flex: viewerMode === 'both' ? 1 : '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#000'
                  }}>
                    {currentRecording.video_url ? (
                      <video
                        ref={videoRef}
                        src={currentRecording.video_url}
                        controls
                        autoPlay
                        onEnded={handleMediaEnded}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    ) : (
                      <p style={{ color: 'white' }}>No video available</p>
                    )}
                  </div>
                )}

                {/* Book Section */}
                {(viewerMode === 'book' || viewerMode === 'both') && (
                  <div style={{ 
                    flex: viewerMode === 'both' ? 1 : '1',
                    background: '#fff',
                    borderRadius: '0.5rem',
                    overflow: 'hidden'
                  }}>
                    {currentRecording.book?.pdf_url ? (
                      <iframe
                        src={currentRecording.book.pdf_url.trim()}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                        title={currentRecording.book.title}
                      />
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        fontSize: '1.25rem',
                        color: '#6b7280'
                      }}>
                        No book PDF available
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Controls */}
          <div style={{
            background: currentRecording.is_ai 
              ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' 
              : (isBedtimeMode ? 'linear-gradient(135deg, #1e1b4b, #312e81)' : 'rgba(0, 0, 0, 0.8)'),
            padding: '1rem 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Button
              onClick={handlePrevious}
              disabled={currentlyPlaying === 0}
              style={{
                background: currentlyPlaying === 0 ? '#4b5563' : (currentRecording.is_ai ? '#6d28d9' : (isBedtimeMode ? '#fbbf24' : '#db2777')),
                color: 'white',
                opacity: currentlyPlaying === 0 ? 0.5 : 1,
                cursor: currentlyPlaying === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous
            </Button>

            <div style={{ color: 'white', fontSize: '0.875rem' }}>
              Story {currentlyPlaying + 1} of {recordings.length}
            </div>

            <Button
              onClick={handleNext}
              style={{
                background: currentRecording.is_ai ? '#6d28d9' : (isBedtimeMode ? '#fbbf24' : '#db2777'),
                color: 'white'
              }}
            >
              {currentlyPlaying < recordings.length - 1 ? 'Next →' : 'Finish'}
            </Button>
          </div>
        </div>
      )}

      {/* Playlist Modal - remains largely the same */}
      {showPlaylistModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <Card style={{
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                Edit Playlist Order
              </h2>
              <Button
                onClick={() => setShowPlaylistModal(false)}
                variant="ghost"
                style={{ fontSize: '1.5rem' }}
              >
                ×
              </Button>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <Button
                onClick={shufflePlaylist}
                variant="secondary"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              >
                <Shuffle size={16} /> Shuffle
              </Button>
              <Button
                onClick={resetPlaylistOrder}
                variant="secondary"
                style={{ flex: 1 }}
              >
                Reset Order
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {playlistOrder.map((recordingIndex, playIndex) => {
                const recording = recordings[recordingIndex];
                const isAI = recording.is_ai;
                
                return (
                  <div
                    key={recording.id}
                    style={{
                      padding: '1rem',
                      background: isAI ? 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' : '#f9fafb',
                      borderRadius: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      border: isAI ? '1px solid #c4b5fd' : '1px solid #e5e7eb',
                      position: 'relative'
                    }}
                  >
                    {isAI && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: '#8b5cf6',
                        color: 'white',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '700'
                      }}>
                        AI
                      </div>
                    )}

                    <div style={{
                      background: isAI ? '#8b5cf6' : '#db2777',
                      color: 'white',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      flexShrink: 0
                    }}>
                      {playIndex + 1}
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {recording.book?.title || 'Untitled'}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: isAI ? '#8b5cf6' : '#6b7280' }}>
                        {isAI 
                          ? `AI: ${recording.voice_name}`
                          : `by ${recording.reader?.first_name} ${recording.reader?.last_name}`
                        }
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                      <button
                        onClick={() => moveUp(playIndex)}
                        disabled={playIndex === 0}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: playIndex === 0 ? '#e5e7eb' : (isAI ? '#8b5cf6' : '#db2777'),
                          color: playIndex === 0 ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: playIndex === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveDown(playIndex)}
                        disabled={playIndex === playlistOrder.length - 1}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: playIndex === playlistOrder.length - 1 ? '#e5e7eb' : (isAI ? '#8b5cf6' : '#db2777'),
                          color: playIndex === playlistOrder.length - 1 ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: playIndex === playlistOrder.length - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <Button
                onClick={() => setShowPlaylistModal(false)}
                style={{
                  width: '100%',
                  background: '#db2777',
                  color: 'white'
                }}
              >
                Done
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};